import { Link } from "react-router-dom";
import { FEATURE_NAMES, PLAN_NAMES, day, num, usd, when } from "@/lib/staff";
import { Badge, Card, Row, Table } from "@/components/staff/ui";
import { AuditChange, KindBadge, actionLabel, modelColumns } from "@/components/staff/bits";
import { RemovePass } from "@/components/staff/StudentActions";

export function ProfileCard({ p }) {
  const methods = [p.has_password && "Password", p.google && "Google"].filter(Boolean).join(" · ") || "None";
  return (
    <Card title="Account" testid="student-profile-card">
      <Row label="Signed up" testid="student-signed-up">{day(p.created_at)}</Row>
      <Row label="Last active" testid="student-last-active">{when(p.last_seen_at)}</Row>
      <Row label="Time zone">{p.timezone || "—"}</Row>
      <Row label="Sign-in methods" testid="student-sign-in-methods">{methods}</Row>
      {p.suspended && <Row label="Suspended" testid="student-suspended-info">{when(p.suspended.at)} by {p.suspended.by}{p.suspended.reason ? ` · "${p.suspended.reason}"` : ""}{p.suspended.renewal_stopped ? " · renewal stopped" : ""}</Row>}
      {p.deletion && <Row label="Deletion" testid="student-deletion-info">Deletes on {day(p.deletion.purge_at)}{p.deletion.by_staff ? ` · scheduled by ${p.deletion.by_staff}` : " · asked by the student"}</Row>}
    </Card>
  );
}

function Subscription({ s }) {
  if (!s) return <Row label="Subscription">None</Row>;
  const ends = s.cancel_at_period_end ? "Ends" : "Renews";
  return (
    <Row label="Subscription" testid="student-subscription">
      {PLAN_NAMES[s.tier]} {s.interval} · {s.status} · {ends} {day(s.current_period_end)}{s.scheduled_tier ? ` · then ${PLAN_NAMES[s.scheduled_tier]}` : ""}
    </Row>
  );
}

function Passes({ d, onChanged }) {
  const passes = d.plan.passes || [];
  if (!passes.length) return <Row label="Passes">None</Row>;
  return (
    <div className="py-2 border-t border-[#162236] space-y-2" data-testid="student-passes">
      <p className="text-sm text-neutral-500">Passes</p>
      {passes.map((x) => (
        <div key={x.pass_id} className="flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`student-pass-${x.pass_id}`}>
          <span>{x.name} · {x.state.replace("_", " ")}{x.ends_at ? ` · ends ${when(x.ends_at)}` : ""} {x.given_by_staff && <Badge tone="sky">Given by staff</Badge>}</span>
          {x.given_by_staff && d.can_act && <RemovePass uid={d.profile.user_id} pass={x} onDone={onChanged} />}
        </div>
      ))}
    </div>
  );
}

export function PlanCard({ d, onChanged }) {
  const { plan, profile } = d;
  const daily = plan.daily || {};
  return (
    <Card title="Plan" testid="student-plan-card">
      <Row label="Using now" testid="student-plan-tier">{PLAN_NAMES[plan.tier]}{plan.base_tier !== plan.tier ? ` (base: ${PLAN_NAMES[plan.base_tier] || plan.base_tier})` : ""}</Row>
      <Subscription s={plan.subscription} />
      <Row label="Free plan from staff" testid="student-comp-plan">{profile.comp_plan ? `${PLAN_NAMES[profile.comp_plan.tier]} until ${day(profile.comp_plan.ends_at)}` : "None"}</Row>
      <Passes d={d} onChanged={onChanged} />
      <Row label="Features" testid="student-features">
        <span className="flex flex-wrap justify-end gap-1">
          {Object.entries(plan.features || {}).map(([f, on]) => <Badge key={f} tone={on ? "green" : "neutral"}>{FEATURE_NAMES[f] || f}{f in profile.feature_overrides ? " · set by staff" : ""}</Badge>)}
        </span>
      </Row>
      {Object.entries(daily).map(([k, v]) => (
        <Row key={k} label={k === "voice" ? "Listen & Mic today" : "Draw it today"} testid={`student-daily-${k}`}>{v.used} of {v.limit}{v.personal ? " (personal limit)" : ""}</Row>
      ))}
    </Card>
  );
}

export function UsageCard({ u }) {
  const withCost = "est_cost" in u.totals;
  return (
    <Card title="AI usage, last 30 days" testid="student-usage-card">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3 text-sm">
        <p><span className="block text-xs text-neutral-500">Requests</span><span className="tabular-nums">{num(u.totals.requests)}</span></p>
        <p><span className="block text-xs text-neutral-500">Failed</span><span className="tabular-nums">{num(u.totals.failed)}</span></p>
        <p><span className="block text-xs text-neutral-500">Tokens (est.)</span><span className="tabular-nums">{num(u.totals.tokens)}</span></p>
        {withCost && <p><span className="block text-xs text-neutral-500">Estimated AI cost</span><span className="tabular-nums" data-testid="student-est-cost">{usd(u.totals.est_cost, 4)}</span></p>}
      </div>
      <Table testid="student-usage-models" columns={modelColumns(withCost)} rows={u.models} rowKey={(r) => r.model} empty="No AI requests in the last 30 days." maxH="max-h-72" />
    </Card>
  );
}

const CHAT_COLS = [
  { key: "title", label: "Chat title", render: (r) => <span className="line-clamp-1">{r.title || "Untitled"}</span> },
  { key: "messages", label: "Messages", right: true, render: (r) => num(r.messages) },
  { key: "updated_at", label: "Last activity", right: true, render: (r) => when(r.updated_at || r.created_at) },
];

export function ChatsCard({ chats }) {
  const rows = chats.items.map((c, i) => ({ ...c, k: String(i) }));
  return (
    <Card title={`Chats (${num(chats.total)})`} testid="student-chats-card">
      <p className="text-xs text-neutral-500 mb-3">Titles and dates only. Messages, files and answers stay private to the student.</p>
      <Table testid="student-chats" columns={CHAT_COLS} rows={rows} rowKey={(r) => r.k} empty="No chats yet." maxH="max-h-72" />
    </Card>
  );
}

const AI_COLS = [
  { key: "created_at", label: "When", render: (r) => <span className="whitespace-nowrap text-xs">{when(r.created_at)}</span> },
  { key: "kind", label: "Kind", render: (r) => r.kind },
  { key: "model", label: "Model", render: (r) => <span className="font-mono text-xs">{r.model}</span> },
  { key: "status", label: "Result", render: (r) => <Badge tone={{ ok: "green", failed: "red" }[r.status] || "neutral"}>{r.status}</Badge> },
  { key: "ms", label: "Time", right: true, render: (r) => `${(r.ms / 1000).toFixed(1)} s` },
];

export const ActivityCard = ({ rows }) => (
  <Card title="Recent AI activity" testid="student-activity-card">
    <Table testid="student-recent-ai" columns={AI_COLS} rows={rows.map((r, i) => ({ ...r, k: String(i) }))} rowKey={(r) => r.k} empty="No AI activity yet." maxH="max-h-72" />
  </Card>
);

export function ErrorsCard({ uid, rows }) {
  return (
    <Card title="Recent errors" testid="student-errors-card"
      action={<Link to={`/staff/errors?user_id=${uid}`} className="text-xs text-sky-300 hover:underline" data-testid="student-errors-link">See all</Link>}>
      {!rows.length && <p className="text-sm text-neutral-500">No errors for this student.</p>}
      <ul className="space-y-2.5">
        {rows.map((e) => (
          <li key={e.error_id} className="text-sm">
            <div className="flex items-center gap-2"><KindBadge kind={e.kind} /><span className="text-xs text-neutral-500">{when(e.created_at)} · {e.area}</span></div>
            <p className="text-neutral-300 mt-1">{e.message}</p>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export function AuditCard({ uid, rows }) {
  return (
    <Card title="Audit trail" testid="student-audit-card"
      action={rows && <Link to={`/staff/audit?target_id=${uid}`} className="text-xs text-sky-300 hover:underline" data-testid="student-audit-link">Full log</Link>}>
      {!rows && <p className="text-sm text-neutral-500">Needs the "See the audit log" permission.</p>}
      {rows && !rows.length && <p className="text-sm text-neutral-500">No staff activity yet.</p>}
      <ul className="space-y-2.5">
        {(rows || []).map((a) => (
          <li key={a.audit_id} className="text-sm">
            <p><span className="text-neutral-100">{actionLabel(a.action)}</span> <span className="text-xs text-neutral-500">· {a.actor_email} · {when(a.at)}</span></p>
            {a.reason && <p className="text-xs text-neutral-400 mt-0.5">"{a.reason}"</p>}
            <AuditChange a={a} />
          </li>
        ))}
      </ul>
    </Card>
  );
}

export function DevicesCard({ devices, sessions }) {
  return (
    <Card title="Devices and sessions" testid="student-devices-card">
      <p className="text-xs text-neutral-500 mb-3" data-testid="student-open-sessions">{num(sessions)} open session{sessions === 1 ? "" : "s"} in total. Devices used in the last 30 days:</p>
      {!devices.length && <p className="text-sm text-neutral-500">No active devices in the last 30 days.</p>}
      <ul className="space-y-2.5">
        {devices.map((x, i) => (
          <li key={`${x.created_at}-${i}`} className="text-sm">
            <p className="text-neutral-100">{x.device} <span className="text-xs text-neutral-500">· {x.kind === "google" ? "Google" : "Password"}</span></p>
            <p className="text-xs text-neutral-500">Last seen {when(x.last_seen)} · {x.ip || "unknown network"}</p>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export function PlanHistoryCard({ rows }) {
  return (
    <Card title="Plan history" testid="student-plan-history">
      <ul className="space-y-2">
        {rows.map((e, i) => (
          <li key={`${e.at}-${i}`} className="flex items-start justify-between gap-4 text-sm" data-testid={`student-plan-history-${i}`}>
            <span className="text-neutral-200">{e.what}</span><span className="text-xs text-neutral-500 whitespace-nowrap">{when(e.at)}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

const PAY_TONE = { paid: "green", refunded: "amber" };
const PAY_COLS = [
  { key: "at", label: "When", render: (r) => <span className="whitespace-nowrap text-xs">{when(r.at)}</span> },
  { key: "what", label: "For", render: (r) => r.what },
  { key: "amount", label: "Amount", right: true, render: (r) => `${r.currency === "USD" ? "$" : `${r.currency} `}${Number(r.amount || 0).toFixed(2)}` },
  { key: "status", label: "Status", right: true, render: (r) => <Badge tone={PAY_TONE[r.status] || "neutral"}>{r.status}{r.mode === "test" ? " · test" : ""}</Badge> },
];

export function PaymentsCard({ rows }) {
  return (
    <Card title="Payments and invoices" testid="student-payments-card">
      {rows ? <Table testid="student-payments" columns={PAY_COLS} rows={rows} rowKey={(r) => r.id} empty="No payments yet." maxH="max-h-72" />
        : <p className="text-sm text-neutral-500">Needs the "See usage and billing" permission.</p>}
    </Card>
  );
}
